import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Form, Image } from 'react-bootstrap';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faInfoCircle,
    faTrash
} from '@fortawesome/free-solid-svg-icons';

import { useLanguage } from '../contexts/LanguageContext';
import { useMode } from '../contexts/ModeContext';
import { useResto } from '../contexts/RestoContext';
import { useAuth } from '../contexts/AuthContext';
import { ucfirst } from '../utils/utils';
import config from '../config/config';
import {
    setFlashMessage,
    useWebsite
} from '../contexts/WebsiteContext';

const Info = () => {
    const { t } = useTranslation();
    const { activeLanguage } = useLanguage();
    const { restoData, setRestoData } = useResto();
    const { isModeAdmin } = useMode();
    const { setIsLoading } = useWebsite();
    const { token } = useAuth();

    // ✅ SAFE fallback
    const availableLanguages =
        restoData?.availableLanguages || [];

    const { apiUrl } = config;
    const apiPath = `${apiUrl}resto`;

    const [editModalOpen, setEditModalOpen] =
        useState(false);

    const [editedInfos, setEditedInfos] =
        useState({});

    // ✅ Safe initialization
    useEffect(() => {
        if (!restoData) return;

        const editedInfosCopy = {};

        availableLanguages.forEach(language => {
            const infoKey =
                `info${ucfirst(language)}`;

            editedInfosCopy[infoKey] =
                restoData?.[infoKey] || '';
        });

        setEditedInfos(editedInfosCopy);

    }, [restoData, activeLanguage]); 
    // ✅ removed availableLanguages from deps

    const handleOpenEditModal = () => {
        setEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setEditModalOpen(false);
    };

    const handleEditSubmit = async () => {
        if (!restoData?.website) return;

        setIsLoading(true);

        try {
            const response = await axios.put(
                `${apiPath}/${restoData.website}`,
                editedInfos,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setRestoData(response.data);
            handleCloseEditModal();
            setFlashMessage(
                t('SUCCESS_UPDATED_MESSAGE'),
                'success'
            );

        } catch (error) {
            setFlashMessage(
                t('ERROR_MESSAGE'),
                'error'
            );
        }

        setIsLoading(false);
    };

    const handleTrashClick = async () => {
        if (!restoData?.website) return;

        setIsLoading(true);

        try {
            const emptyInfos = {};

            availableLanguages.forEach(language => {
                emptyInfos[
                    `info${ucfirst(language)}`
                ] = '';
            });

            const response = await axios.put(
                `${apiPath}/${restoData.website}`,
                emptyInfos,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setRestoData(response.data);

            setFlashMessage(
                t('SUCCESS_DELETED_MESSAGE'),
                'success'
            );

        } catch (error) {
            setFlashMessage(
                t('ERROR_MESSAGE'),
                'error'
            );
        }

        setIsLoading(false);
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;

        setEditedInfos(prevState => ({
            ...prevState,
            [name]: value || ''
        }));
    };

    return (
        <div>

            {/* PUBLIC VIEW */}
            {!isModeAdmin &&
                editedInfos?.[
                    `info${ucfirst(activeLanguage)}`
                ] && (
                    <p
                        className="kubuni-frame"
                        style={{ paddingBottom: '10px' }}
                    >
                        <FontAwesomeIcon
                            icon={faInfoCircle}
                        />{' '}
                        {editedInfos[
                            `info${ucfirst(activeLanguage)}`
                        ]}
                    </p>
                )
            }

            {/* ADMIN VIEW */}
            {isModeAdmin && (
                <>
                    <h2>
                        Info

                        <Button
                            variant="primary"
                            className="btn-sm"
                            style={{ marginLeft: '10px' }}
                            onClick={handleOpenEditModal}
                        >
                            <FontAwesomeIcon
                                icon={faEdit}
                            />
                        </Button>

                        <Button
                            variant="primary"
                            className="btn-sm"
                            style={{ marginLeft: '10px' }}
                            onClick={handleTrashClick}
                        >
                            <FontAwesomeIcon
                                icon={faTrash}
                            />
                        </Button>
                    </h2>

                    {availableLanguages.map(language => (
                        <div key={language}>
                            <p>
                                <Image
                                    src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`}
                                    className="flag-label"
                                    alt={language}
                                />

                                {' '}
                                {editedInfos?.[
                                    `info${ucfirst(language)}`
                                ] || ' Aucune information pour cette langue'}
                            </p>
                        </div>
                    ))}
                </>
            )}

            {/* EDIT MODAL */}
            <Modal
                show={editModalOpen}
                onHide={handleCloseEditModal}
                size="xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t('EDIT')}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form>
                        {availableLanguages.map(language => (
                            <div key={language}>
                                <Form.Group
                                    controlId={`editInfo${ucfirst(language)}`}
                                >
                                    <Form.Label>
                                        <Image
                                            src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`}
                                            className="flag-label"
                                            alt={language}
                                        />
                                    </Form.Label>

                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name={`info${ucfirst(language)}`}
                                        value={
                                            editedInfos?.[
                                                `info${ucfirst(language)}`
                                            ] || ''
                                        }
                                        onChange={handleEditInputChange}
                                    />
                                </Form.Group>
                            </div>
                        ))}
                    </Form>
                </Modal.Body>

                <Modal.Footer className="justify-content-center">
                    <Button
                        variant="secondary"
                        onClick={handleCloseEditModal}
                    >
                        {t('CANCEL')}
                    </Button>

                    <Button
                        variant="primary"
                        onClick={handleEditSubmit}
                    >
                        {t('CONFIRM')}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default Info;